import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

const LINKED_OPTION_PROXY_PREFIX = '/apps/pmecake';
const LINKED_OPTION_CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_PREFIX = 'pmecake:linked-option:group:';
const LINKED_OPTION_SECTION_SELECTOR =
  'king-linked-options-product[data-gid], #king-linked-options-section[data-gid]';

/** @typedef {{ tier?: number|null, label?: string, hex?: string, nextDeliveryDate?: string|null, updating?: boolean }} StockStatus */
/** @typedef {{ label?: string, label_with_date?: string }} StockTierLabels */
/** @typedef {{ value?: string, color_type?: string, color_one?: string, color_two?: string, color_image?: string, image?: string, badge?: string }} LinkedOptionValue */
/** @typedef {{ id?: string, variant?: string[], title?: string, handle?: string, images?: string[], available?: boolean, published_online_store?: boolean, published_in_catalog?: boolean, status?: string, sku?: string|null, cart_variant_id?: string|null, available_quantity?: number|null, available_at_location?: boolean, inner_quantity?: string|null, master_quantity?: string|null, price?: string|null, currency_code?: string|null, stock_status?: StockStatus|null, option_value?: LinkedOptionValue }} LinkedOptionProduct */
/** @typedef {{ id?: string, group_name?: string, group_type?: string, status?: string, products: LinkedOptionProduct[], created_at?: string }} LinkedOptionGroupData */
/** @typedef {{ variantId: string, productId: string, quantity: number }} CustomerLastOrderLine */
/** @typedef {{ orderId: string, orderName: string, createdAt: string, lines: CustomerLastOrderLine[] }} CustomerLastOrder */

/** @typedef {{ data: LinkedOptionGroupData | null, fetchFailed: boolean }} LinkedOptionGroupFetchResult */
/** @typedef {{ productId?: string, promise?: Promise<LinkedOptionGroupFetchResult> }} PrefetchedLite */

/**
 * @param {string} productId
 * @returns {Promise<LinkedOptionGroupFetchResult> | null}
 */
function readPrefetchedLite(productId) {
  const prefetched = /** @type {{ pmecakeLinkedOptionLite?: PrefetchedLite }} */ (window)
    .pmecakeLinkedOptionLite;
  if (!prefetched?.promise) return null;
  if (String(prefetched.productId || '') !== String(productId)) return null;
  return prefetched.promise;
}

/** @type {Map<string, Promise<LinkedOptionGroupFetchResult>>} */
const pendingByGid = new Map();

/**
 * @param {unknown} data
 * @returns {data is LinkedOptionGroupData}
 */
function isValidLinkedOptionGroupData(data) {
  return Boolean(
    data &&
      typeof data === 'object' &&
      'products' in data &&
      Array.isArray(/** @type {{ products?: unknown }} */ (data).products)
  );
}

/**
 * @param {string} gid
 * @param {string} [countryCode]
 * @returns {string}
 */
function getCacheKey(gid, countryCode = '') {
  const id = (gid || '').trim();
  const market = (countryCode || '').trim().toUpperCase();
  return `${CACHE_PREFIX}${id}:${market || 'default'}`;
}

/**
 * @param {string} gid
 * @param {string} [countryCode]
 * @returns {LinkedOptionGroupData | null}
 */
function readCache(gid, countryCode = '') {
  const id = (gid || '').trim();
  if (!id) return null;

  try {
    const key = getCacheKey(id, countryCode);
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;

    const { expiresAt, data } = JSON.parse(raw);
    if (!expiresAt || Date.now() > expiresAt || !isValidLinkedOptionGroupData(data)) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * @param {string} gid
 * @param {LinkedOptionGroupData} data
 * @param {string} [countryCode]
 */
function writeCache(gid, data, countryCode = '') {
  const id = (gid || '').trim();
  if (!id || !isValidLinkedOptionGroupData(data)) return;

  try {
    window.sessionStorage.setItem(
      getCacheKey(id, countryCode),
      JSON.stringify({ expiresAt: Date.now() + LINKED_OPTION_CACHE_TTL_MS, data })
    );
  } catch {}
}

/**
 * @param {string} gid
 * @param {string} [proxyPrefix]
 * @param {string} [countryCode]
 * @param {boolean} [useCache]
 * @param {boolean} [lite]
 * @returns {Promise<LinkedOptionGroupFetchResult>}
 */
async function fetchLinkedOptionGroup(
  gid,
  proxyPrefix = LINKED_OPTION_PROXY_PREFIX,
  countryCode = '',
  useCache = true,
  lite = false
) {
  const id = (gid || '').trim();
  if (!id) return { data: null, fetchFailed: false };

  const market = (countryCode || '').trim().toUpperCase();
  const cacheKey = `${getCacheKey(id, market)}:${lite ? 'lite' : 'full'}`;

  if (useCache && !lite) {
    const cached = readCache(id, market);
    if (cached) return { data: cached, fetchFailed: false };
  }

  const pending = pendingByGid.get(cacheKey);
  if (pending) return pending;

  const prefix = (proxyPrefix || LINKED_OPTION_PROXY_PREFIX).replace(/\/$/, '');
  const url = new URL(`${prefix}/linked-options/groups/${id}`, window.location.origin);
  if (market) url.searchParams.set('countryCode', market);
  if (lite) url.searchParams.set('lite', '1');

  const request = fetch(url.toString(), { headers: { Accept: 'application/json' } })
    .then(async (response) => {
      if (!response.ok) return { data: null, fetchFailed: true };
      const body = await response.json();
      const data =
        body && typeof body === 'object' && 'data' in body
          ? /** @type {{ data?: unknown }} */ (body).data
          : null;
      if (!isValidLinkedOptionGroupData(data)) return { data: null, fetchFailed: true };
      if (useCache && !lite) writeCache(id, data, market);
      return { data, fetchFailed: false };
    })
    .catch(() => ({ data: null, fetchFailed: true }))
    .finally(() => {
      pendingByGid.delete(cacheKey);
    });

  pendingByGid.set(cacheKey, request);
  return request;
}

/**
 * @param {string} productId
 * @param {string} [proxyPrefix]
 * @param {string} [countryCode]
 * @returns {Promise<LinkedOptionGroupFetchResult>}
 */
async function fetchLinkedOptionGroupByProduct(
  productId,
  proxyPrefix = LINKED_OPTION_PROXY_PREFIX,
  countryCode = ''
) {
  const id = String(productId || '').trim();
  if (!/^\d+$/.test(id)) return { data: null, fetchFailed: false };

  const prefix = (proxyPrefix || LINKED_OPTION_PROXY_PREFIX).replace(/\/$/, '');
  const url = new URL(`${prefix}/linked-options/groups`, window.location.origin);
  const market = (countryCode || '').trim().toUpperCase();
  if (market) url.searchParams.set('countryCode', market);
  url.searchParams.set('productId', id);
  url.searchParams.set('lite', '1');

  try {
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) return { data: null, fetchFailed: true };
    const body = await response.json();
    const data =
      body && typeof body === 'object' && 'data' in body
        ? /** @type {{ data?: unknown }} */ (body).data
        : null;
    if (data == null) return { data: null, fetchFailed: false };
    if (!isValidLinkedOptionGroupData(data)) return { data: null, fetchFailed: true };
    return { data, fetchFailed: false };
  } catch {
    return { data: null, fetchFailed: true };
  }
}

/**
 * @param {string} gid
 * @param {string} [proxyPrefix]
 * @param {string} [countryCode]
 * @param {string[]} productIds
 * @param {string} [companyLocationId]
 * @returns {Promise<LinkedOptionGroupFetchResult>}
 */
async function fetchLinkedOptionEnrichment(
  gid,
  proxyPrefix = LINKED_OPTION_PROXY_PREFIX,
  countryCode = '',
  productIds = [],
  companyLocationId = ''
) {
  const id = (gid || '').trim();
  const ids = productIds.map((value) => String(value).trim()).filter((value) => /^\d+$/.test(value));
  if (!id || !ids.length) return { data: null, fetchFailed: false };

  const prefix = (proxyPrefix || LINKED_OPTION_PROXY_PREFIX).replace(/\/$/, '');
  const url = new URL(`${prefix}/linked-options/groups/${id}`, window.location.origin);
  const market = (countryCode || '').trim().toUpperCase();
  const location = String(companyLocationId || '').trim();
  if (market) url.searchParams.set('countryCode', market);
  if (location) url.searchParams.set('companyLocationId', location);
  url.searchParams.set('enrich', '1');
  url.searchParams.set('ids', ids.join(','));

  try {
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) return { data: null, fetchFailed: true };
    const body = await response.json();
    const data =
      body && typeof body === 'object' && 'data' in body
        ? /** @type {{ data?: unknown }} */ (body).data
        : null;
    if (!isValidLinkedOptionGroupData(data)) return { data: null, fetchFailed: true };
    return { data, fetchFailed: false };
  } catch {
    return { data: null, fetchFailed: true };
  }
}

/**
 * @param {LinkedOptionGroupData} liteData
 * @param {LinkedOptionProduct[]} enrichProducts
 * @returns {LinkedOptionGroupData}
 */
function mergeLiteAndEnrich(liteData, enrichProducts) {
  const enrichById = new Map(
    enrichProducts.map((product) => [String(product.id ?? ''), product])
  );

  return {
    ...liteData,
    products: (liteData.products || []).map((product) => {
      const extra = enrichById.get(String(product.id ?? ''));
      return extra ? { ...product, ...extra } : product;
    }),
  };
}

/**
 * @param {unknown} data
 * @returns {data is CustomerLastOrder}
 */
function isValidCustomerLastOrder(data) {
  return Boolean(
    data &&
      typeof data === 'object' &&
      'lines' in data &&
      Array.isArray(/** @type {{ lines?: unknown }} */ (data).lines)
  );
}

/**
 * @param {string} [proxyPrefix]
 * @returns {Promise<{ order: CustomerLastOrder | null, error: string | null }>}
 */
async function fetchCustomerLastOrder(proxyPrefix = LINKED_OPTION_PROXY_PREFIX) {
  const prefix = (proxyPrefix || LINKED_OPTION_PROXY_PREFIX).replace(/\/$/, '');
  const url = new URL(`${prefix}/linked-options/last-order`, window.location.origin);

  try {
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in body
          ? String(/** @type {{ error?: unknown }} */ (body).error ?? '')
          : '';
      return {
        order: null,
        error: message || 'Failed to load last order.',
      };
    }

    const order =
      body && typeof body === 'object' && 'order' in body
        ? /** @type {{ order?: unknown }} */ (body).order
        : null;

    if (order != null && !isValidCustomerLastOrder(order)) {
      return { order: null, error: 'Invalid last order response.' };
    }

    return {
      order: isValidCustomerLastOrder(order) ? order : null,
      error: null,
    };
  } catch {
    return { order: null, error: 'Failed to load last order.' };
  }
}

/**
 * Escapes a string for safe HTML insertion.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string|null|undefined} value
 * @returns {number|null}
 */
function parsePackSize(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {string|null|undefined} inner
 * @param {string|null|undefined} master
 * @returns {boolean}
 */
function isSamePackQuantity(inner, master) {
  const innerSize = parsePackSize(inner);
  const masterSize = parsePackSize(master);
  return innerSize != null && masterSize != null && innerSize === masterSize;
}

/**
 * @param {string|null|undefined} inner
 * @param {string|null|undefined} master
 * @returns {boolean}
 */
function hasUsableMasterPack(inner, master) {
  const masterSize = parsePackSize(master);
  if (masterSize == null) return false;
  return !isSamePackQuantity(inner, master);
}

/**
 * @param {Array<{ inner_quantity?: string|null, master_quantity?: string|null }>} rows
 * @returns {boolean}
 */
function shouldShowMasterColumn(rows) {
  return rows.some((row) => hasUsableMasterPack(row.inner_quantity, row.master_quantity));
}

/**
 * @param {LinkedOptionProduct | undefined} product
 * @returns {number|null}
 */
function parseProductPrice(product) {
  const raw = product?.price;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * @param {LinkedOptionGroupData | null} data
 * @param {Element | null} [section]
 * @returns {boolean}
 */
function isSingleGroup(data, section) {
  const fromApi = String(data?.group_type || '').trim().toUpperCase();
  if (fromApi) return fromApi === 'SINGLE';

  const fromSection = (section instanceof HTMLElement ? section.dataset.groupType : '') || '';
  return fromSection.toUpperCase() === 'SINGLE';
}

/**
 * @typedef {object} VariantMeta
 * @property {number|string} id
 * @property {number|string} [product_id]
 * @property {string} title
 * @property {boolean} available
 * @property {boolean} [available_at_location]
 * @property {number|null} [inventory_quantity]
 * @property {string|null|undefined} inner_quantity
 * @property {string|null|undefined} master_quantity
 * @property {string|null} [image]
 */

/**
 * @typedef {object} RenderedProductEntry
 * @property {LinkedOptionProduct} [product]
 * @property {string} rowValue
 * @property {number} units
 * @property {number} innerPacks
 * @property {number} masterPacks
 * @property {number|null} innerPackSize
 * @property {number|null} masterPackSize
 * @property {number} quantity
 * @property {string} sku
 * @property {number|null} price
 * @property {string} variantId
 */

class LinkedOptionsB2bMatrixTable extends HTMLElement {
  /** @type {Map<string, RenderedProductEntry>} */
  #renderedProducts = new Map();

  /** @type {VariantMeta[]} */
  #variantMeta = [];

  /** @type {AbortController | null} */
  #inputAbort = null;

  /** @type {AbortController | null} */
  #docAbort = null;

  /** @type {string} */
  #currencyCode = '';

  /** @type {Record<string, string>} */
  #i18n = {};

  /** @type {Record<string, StockTierLabels>} */
  #stockTierLabels = {};

  /** @type {boolean} */
  #showMasterColumn = false;

  /** @type {string} */
  #noImageUrl = '';

  /** @type {boolean} */
  #initStarted = false;

  connectedCallback() {
    this.#currencyCode = (this.dataset.currency || '').trim().toUpperCase();
    this.#noImageUrl = (this.dataset.noImageUrl || '').trim();
    this.#i18n = this.#readI18nJson();
    this.#stockTierLabels = this.#readStockTierLabelsJson();
    this.#bindEvents();
    this.#listenForLinkedOptionEvent();
    this.#applyLoadLastOrderGate();
    this.#initFromProductId().catch(() => {
      this.#initStarted = false;
      this.#tryInitFromDom();
    });
  }

  disconnectedCallback() {
    this.#inputAbort?.abort();
    this.#docAbort?.abort();
  }

  #listenForLinkedOptionEvent() {
    this.#docAbort?.abort();
    this.#docAbort = new AbortController();

    document.addEventListener(
      'king-linked-option-product-loaded',
      () => {
        this.#tryInitFromDom();
      },
      { signal: this.#docAbort.signal }
    );
  }

  #tryInitFromDom() {
    if (this.dataset.initialized === 'true') return;

    const section = document.querySelector(LINKED_OPTION_SECTION_SELECTOR);
    if (section instanceof HTMLElement) this.#initFromSection(section);
  }

  /**
   * @returns {Record<string, string>}
   */
  #readI18nJson() {
    const script = this.querySelector('[data-ref="i18nJson"]');
    if (!script || !script.textContent) return {};

    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @returns {Record<string, StockTierLabels>}
   */
  #readStockTierLabelsJson() {
    const script = this.querySelector('[data-ref="stockTierLabelsJson"]');
    if (!script || !script.textContent) return {};

    try {
      const parsed = JSON.parse(script.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [vars]
   * @returns {string}
   */
  #t(key, vars = {}) {
    let str = this.#i18n[key] || key;
    for (const [name, value] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), String(value));
    }
    return str;
  }

  /**
   * @returns {VariantMeta[]}
   */
  #readVariantMetaJson() {
    const script = this.querySelector('[data-ref="variantMetaJson"]');
    if (!script || !script.textContent) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @returns {string}
   */
  #getCountryCode() {
    const fromDataset = (this.dataset.countryCode || '').trim().toUpperCase();
    if (fromDataset) return fromDataset;

    const shopify = /** @type {{ country?: string }} */ (window.Shopify);
    const fromShopify = (shopify?.country || '').trim().toUpperCase();
    return fromShopify || '';
  }

  /**
   * @returns {string}
   */
  #getCompanyLocationId() {
    return (this.dataset.companyLocationId || '').trim();
  }

  async #initFromProductId() {
    if (this.dataset.initialized === 'true' || this.#initStarted) return;

    const productId = (this.dataset.productId || '').trim();
    if (!productId) {
      this.#tryInitFromDom();
      return;
    }

    this.#initStarted = true;
    this.#variantMeta = this.#readVariantMetaJson();

    const proxyPrefix = this.dataset.proxyPrefix || '/apps/pmecake';
    const countryCode = this.#getCountryCode();
    const lite = await (readPrefetchedLite(productId) ||
      fetchLinkedOptionGroupByProduct(productId, proxyPrefix, countryCode));

    if (!lite.data) {
      this.#initStarted = false;
      this.#tryInitFromDom();
      return;
    }

    const gid = String(lite.data.id || '').trim();
    const section = document.createElement('div');
    section.dataset.gid = gid;
    section.dataset.groupType = String(lite.data.group_type || 'SINGLE');
    await this.#continueAfterLite(lite, gid, section);
  }

  /**
   * @param {Element} section
   */
  async #initFromSection(section) {
    if (this.dataset.initialized === 'true' || this.#initStarted) return;

    const gid = (section instanceof HTMLElement ? section.dataset.gid : '') || '';
    if (!gid) return;

    this.#initStarted = true;
    this.#variantMeta = this.#readVariantMetaJson();

    const proxyPrefix = this.dataset.proxyPrefix || '/apps/pmecake';
    const countryCode = this.#getCountryCode();
    const useCache = this.dataset.cacheEnabled !== 'false';
    const cached = useCache ? readCache(gid, countryCode) : null;

    if (cached && isSingleGroup(cached, section)) {
      this.#renderTable(cached);
      this.#applyLoadLastOrderGate();
      this.dataset.initialized = 'true';
      this.#refreshPrices(gid, proxyPrefix, countryCode).catch(() => {});
      return;
    }

    const lite = await fetchLinkedOptionGroup(gid, proxyPrefix, countryCode, false, true).catch(
      () => ({ data: null, fetchFailed: true })
    );
    await this.#continueAfterLite(lite, gid, section);
  }

  /**
   * @param {LinkedOptionGroupFetchResult} lite
   * @param {string} gid
   * @param {Element | null} section
   */
  async #continueAfterLite(lite, gid, section) {
    const proxyPrefix = this.dataset.proxyPrefix || '/apps/pmecake';
    const countryCode = this.#getCountryCode();
    const useCache = this.dataset.cacheEnabled !== 'false';

    if (lite.data && isSingleGroup(lite.data, section)) {
      this.#renderTable(lite.data);
      this.#applyLoadLastOrderGate();
      this.dataset.initialized = 'true';
    } else if (lite.data && String(lite.data.group_type || '').trim() && !isSingleGroup(lite.data, section)) {
      this.dataset.initialized = 'true';
      return;
    }

    const groupId = gid || String(lite.data?.id || '').trim();
    const productIds = (lite.data?.products || [])
      .map((product) => String(product.id ?? '').trim())
      .filter((id) => /^\d+$/.test(id));

    if (!productIds.length || !groupId) {
      if (lite.fetchFailed && this.#renderedProducts.size === 0) {
        this.#showGroupLoadError();
      }
      if (this.#renderedProducts.size > 0 || String(lite.data?.group_type || '').trim()) {
        this.dataset.initialized = 'true';
        return;
      }
      this.#initStarted = false;
      return;
    }

    const enrich = await fetchLinkedOptionEnrichment(
      groupId,
      proxyPrefix,
      countryCode,
      productIds,
      this.#getCompanyLocationId()
    );
    if (enrich.fetchFailed && this.#renderedProducts.size === 0) {
      this.#showGroupLoadError();
    }

    if (enrich.data && this.#renderedProducts.size > 0) {
      this.#hydrateFromGroup(enrich.data);
      if (useCache && lite.data) {
        writeCache(groupId, mergeLiteAndEnrich(lite.data, enrich.data.products), countryCode);
      }
      return;
    }

    if (this.#renderedProducts.size > 0) return;

    if (String(lite.data?.group_type || '').trim()) {
      this.dataset.initialized = 'true';
      return;
    }

    this.#initStarted = false;
  }

  /**
   * @param {string} gid
   * @param {string} proxyPrefix
   * @param {string} countryCode
   */
  async #refreshPrices(gid, proxyPrefix, countryCode) {
    const productIds = [...this.#renderedProducts.keys()].filter((id) => /^\d+$/.test(id));
    if (!productIds.length) return;

    const { data } = await fetchLinkedOptionEnrichment(
      gid,
      proxyPrefix,
      countryCode,
      productIds,
      this.#getCompanyLocationId()
    );
    if (!data) return;

    this.#hydrateFromGroup(data);
  }

  /**
   * @param {RenderedProductEntry} entry
   * @param {LinkedOptionProduct | undefined} product
   */
  #rowFromEntry(entry, product) {
    const productId = String(product?.id ?? entry.product?.id ?? '');
    const cartVariantId =
      (product?.cart_variant_id ? String(product.cart_variant_id) : null) ??
      entry.variantId ??
      productId;
    const availableAtLocation = product?.available_at_location !== false;
    const availableQuantity =
      product?.available_quantity != null ? Number(product.available_quantity) : null;
    const innerQuantity =
      product?.inner_quantity != null && product.inner_quantity !== ''
        ? product.inner_quantity
        : entry.product?.inner_quantity;
    const masterQuantity =
      product?.master_quantity != null && product.master_quantity !== ''
        ? product.master_quantity
        : entry.product?.master_quantity;

    return {
      id: productId,
      rowKey: productId,
      cartVariantId,
      title: entry.rowValue,
      sku: (product?.sku || entry.sku || '').trim(),
      available: product?.available !== false && availableAtLocation,
      available_at_location: availableAtLocation,
      inventory_quantity: availableQuantity,
      inner_quantity: innerQuantity,
      master_quantity: masterQuantity,
      product,
    };
  }

  /**
   * @param {Element | undefined} cell
   * @param {number|null} packSize
   * @param {'unit'|'inner'|'master'} packType
   * @param {string} rowKey
   * @param {string} title
   * @param {boolean} disabled
   * @param {string} savedValue
   * @param {boolean} [useUnavailable]
   */
  #patchPackCell(cell, packSize, packType, rowKey, title, disabled, savedValue, useUnavailable = false) {
    if (!(cell instanceof HTMLElement)) return;
    cell.classList.toggle('b2b-order__cell--disabled', disabled || useUnavailable);

    if (useUnavailable) {
      cell.innerHTML = this.#renderPackUnavailable();
      return;
    }

    let input = cell.querySelector(`[data-pack-type="${packType}"]`);
    if (!(input instanceof HTMLInputElement)) {
      cell.innerHTML = this.#renderPackInput(packSize, packType, rowKey, title, disabled);
      input = cell.querySelector('input');
      if (input instanceof HTMLInputElement && !input.disabled) {
        input.value = savedValue;
      }
      return;
    }

    input.disabled = disabled;
    if (disabled) {
      input.setAttribute('aria-disabled', 'true');
    } else {
      input.removeAttribute('aria-disabled');
      input.value = savedValue;
    }

    const inner = cell.querySelector('.b2b-order__pack-cell-inner') || cell;
    let sizeLabel = inner.querySelector('.b2b-order__pack-size');
    const sizeText =
      packType === 'unit'
        ? this.#t('per_unit')
        : packSize != null
          ? this.#t('pack_units', { count: packSize })
          : '';

    if (sizeText) {
      if (!sizeLabel) {
        sizeLabel = document.createElement('span');
        sizeLabel.className = 'b2b-order__pack-size';
        inner.appendChild(sizeLabel);
      }
      sizeLabel.textContent = sizeText;
    } else if (sizeLabel) {
      sizeLabel.remove();
    }
  }

  /**
   * @param {string} rowKey
   * @param {RenderedProductEntry} entry
   * @param {LinkedOptionProduct} product
   * @param {boolean} showMaster
   */
  #hydrateRow(rowKey, entry, product, showMaster) {
    const rowEl = this.querySelector(`tr[data-variant-id="${CSS.escape(rowKey)}"]`);
    if (!(rowEl instanceof HTMLTableRowElement)) return;

    const row = this.#rowFromEntry(entry, product);
    const innerPackSize = parsePackSize(row.inner_quantity);
    const masterPackSize = parsePackSize(row.master_quantity);
    const masterSameAsInner = isSamePackQuantity(row.inner_quantity, row.master_quantity);
    const rowMasterPackSize = showMaster && !masterSameAsInner ? masterPackSize : null;
    const rowUnavailable = row.available_at_location === false || !row.available;
    const unitDisabled = rowUnavailable;
    const innerDisabled = rowUnavailable || innerPackSize == null;
    const masterDisabled = rowUnavailable || masterPackSize == null || masterSameAsInner;
    const unitPrice = parseProductPrice(product);
    const productCurrency = (product.currency_code || '').trim().toUpperCase();
    if (unitPrice != null && productCurrency) {
      this.#currencyCode = productCurrency;
    }

    const savedUnits = String(entry.units);
    const savedInner = String(entry.innerPacks);
    const savedMaster = String(entry.masterPacks);

    entry.product = product;
    entry.sku = row.sku || entry.sku;
    entry.price = unitPrice ?? entry.price;
    entry.variantId = row.cartVariantId;
    entry.innerPackSize = innerPackSize;
    entry.masterPackSize = rowMasterPackSize;

    rowEl.className = rowUnavailable ? 'b2b-order__row b2b-order__row--disabled' : 'b2b-order__row';
    rowEl.dataset.searchTerms = [row.title, row.sku, rowKey, row.cartVariantId]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (row.sku) {
      const details = rowEl.querySelector('.b2b-order__variant-details');
      let skuEl = rowEl.querySelector('.b2b-order__variant-sku');
      if (!skuEl && details) {
        skuEl = document.createElement('span');
        skuEl.className = 'b2b-order__variant-sku';
        details.appendChild(skuEl);
      }
      if (skuEl) skuEl.textContent = row.sku;
    }

    const stockCell = rowEl.querySelector('.b2b-order__cell--stock');
    if (stockCell) {
      stockCell.classList.toggle('b2b-order__cell--unavailable', rowUnavailable);
      stockCell.innerHTML = this.#renderStockStatus(row);
    }

    const packCells = [...rowEl.querySelectorAll('.b2b-order__cell--pack')];
    this.#patchPackCell(packCells[0], null, 'unit', rowKey, entry.rowValue, unitDisabled, savedUnits);
    this.#patchPackCell(
      packCells[1],
      innerPackSize,
      'inner',
      rowKey,
      entry.rowValue,
      innerDisabled,
      savedInner
    );

    let masterCell = packCells[2];
    if (showMaster && !masterCell) {
      rowEl.insertAdjacentHTML(
        'beforeend',
        '<td class="b2b-order__cell b2b-order__cell--pack"></td>'
      );
      masterCell = rowEl.querySelectorAll('.b2b-order__cell--pack')[2];
    }
    if (!showMaster && masterCell) {
      masterCell.remove();
    } else if (showMaster) {
      this.#patchPackCell(
        masterCell,
        masterPackSize,
        'master',
        rowKey,
        entry.rowValue,
        masterDisabled,
        savedMaster,
        masterSameAsInner
      );
    }

    this.#syncEntryFromRow(rowKey);
  }

  /**
   * @param {LinkedOptionGroupData} data
   */
  #hydrateFromGroup(data) {
    const products = Array.isArray(data.products) ? data.products : [];
    const enrichById = new Map(products.map((product) => [String(product.id ?? ''), product]));

    for (const [rowKey] of [...this.#renderedProducts]) {
      const product = enrichById.get(rowKey);
      if (product?.available_at_location !== false && product?.published_in_catalog !== false) continue;

      const rowEl = this.querySelector(`tr[data-variant-id="${CSS.escape(rowKey)}"]`);
      rowEl?.remove();
      this.#renderedProducts.delete(rowKey);
    }

    const remaining = [];
    for (const [rowKey, entry] of this.#renderedProducts) {
      const extra = enrichById.get(rowKey);
      const product = extra ? { ...entry.product, ...extra } : entry.product;
      remaining.push({
        rowKey,
        entry,
        product,
        row: this.#rowFromEntry(entry, product),
      });
    }

    const showMaster = shouldShowMasterColumn(remaining.map((item) => item.row));
    this.#showMasterColumn = showMaster;

    const headerRow = this.querySelector('[data-ref="headerRow"]');
    const masterHeader = headerRow?.querySelector('th:last-child');
    if (masterHeader instanceof HTMLElement) {
      masterHeader.hidden = !showMaster;
    }

    for (const item of remaining) {
      if (!item.product) continue;
      this.#hydrateRow(item.rowKey, item.entry, item.product, showMaster);
    }

    const searchInput = this.querySelector('[data-ref="searchInput"]');
    const query = searchInput instanceof HTMLInputElement ? searchInput.value : '';
    if (query) this.#applySearch(query);

    this.#updateTotals();
  }

  /**
   * @returns {boolean}
   */
  #isCustomerLoggedIn() {
    return this.dataset.customerLoggedIn === 'true';
  }

  #showGroupLoadError() {
    const errorMsg = this.querySelector('[data-ref="errorMsg"]');
    if (!errorMsg) return;

    errorMsg.textContent = this.#t('error_could_not_load_group');
    errorMsg.classList.add('b2b-order__error--visible');
  }

  #applyLoadLastOrderGate() {
    const btnLoad = this.querySelector('[data-ref="btnLoad"]');
    if (!(btnLoad instanceof HTMLButtonElement)) return;

    if (this.#isCustomerLoggedIn()) {
      btnLoad.disabled = false;
      btnLoad.removeAttribute('aria-disabled');
      return;
    }

    btnLoad.disabled = true;
    btnLoad.setAttribute('aria-disabled', 'true');
  }

  /**
   * @param {LinkedOptionGroupData | null} data
   * @returns {Array<VariantMeta & { product?: LinkedOptionProduct, rowKey?: string, cartVariantId?: string, sku?: string }>}
   */
  #buildVariantRows(data) {
    /** @type {Map<string, VariantMeta>} */
    const metaByVariantId = new Map(
      this.#variantMeta.map((variant) => [String(variant.id), variant])
    );

    /** @type {Map<string, VariantMeta>} */
    const metaByProductId = new Map();
    for (const variant of this.#variantMeta) {
      if (variant.product_id != null) {
        metaByProductId.set(String(variant.product_id), variant);
      }
    }

    /** @type {Array<VariantMeta & { product?: LinkedOptionProduct, rowKey?: string, cartVariantId?: string, sku?: string }>} */
    const rows = [];

    /** @type {LinkedOptionProduct[]} */
    const products =
      data !== null && Array.isArray(data.products) ? data.products : [];

    for (const product of products) {
      const productId = String(product.id ?? '');
      if (!productId) continue;
      if (product.available_at_location === false) continue;
      if (product.published_in_catalog === false) continue;

      const meta =
        metaByVariantId.get(productId) ?? metaByProductId.get(productId) ?? null;

      const cartVariantId =
        (product.cart_variant_id ? String(product.cart_variant_id) : null) ??
        (meta ? String(meta.id) : null) ??
        productId;

      const optionValue =
        product &&
        typeof product === 'object' &&
        'option_value' in product &&
        product.option_value &&
        typeof product.option_value === 'object' &&
        'value' in product.option_value
          ? String(/** @type {{ value?: unknown }} */ (product.option_value).value ?? '')
          : '';

      const displayTitle =
        optionValue && !optionValue.startsWith('LinkedOption_')
          ? optionValue
          : product.title || meta?.title || optionValue || productId;

      const availableQuantity =
        product.available_quantity != null
          ? Number(product.available_quantity)
          : meta?.inventory_quantity != null
            ? meta.inventory_quantity
            : null;

      const innerQuantity =
        product.inner_quantity != null && product.inner_quantity !== ''
          ? product.inner_quantity
          : meta?.inner_quantity;

      const masterQuantity =
        product.master_quantity != null && product.master_quantity !== ''
          ? product.master_quantity
          : meta?.master_quantity;

      const sku = (product.sku || '').trim();
      const availableAtLocation = product.available_at_location !== false;

      rows.push({
        id: productId,
        rowKey: productId,
        cartVariantId,
        title: displayTitle,
        sku,
        available:
          product.available !== false && meta?.available !== false && availableAtLocation,
        available_at_location: availableAtLocation,
        inventory_quantity: availableQuantity,
        inner_quantity: innerQuantity,
        master_quantity: masterQuantity,
        product,
      });
    }

    if (products.length > 0) return rows;

    return this.#variantMeta.map((meta) => ({
      ...meta,
      rowKey: String(meta.id),
      cartVariantId: String(meta.id),
    }));
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #resolveThumbUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.hostname === 'thumbnail.getalltool.com') return url;
      parsed.searchParams.set('width', '64');
      parsed.searchParams.delete('height');
      return parsed.toString();
    } catch {
      return url;
    }
  }

  /**
   * @param {VariantMeta & { product?: LinkedOptionProduct, image?: string|null }} row
   * @returns {string|null}
   */
  #getVariantImageUrl(row) {
    const optionImage = (row.product?.option_value?.image || '').trim();
    if (optionImage) return this.#resolveThumbUrl(optionImage);

    const colorImage = (row.product?.option_value?.color_image || '').trim();
    if (colorImage) return this.#resolveThumbUrl(colorImage);

    const fromProduct = (row.product?.images?.[0] || '').trim();
    if (fromProduct) return this.#resolveThumbUrl(fromProduct);

    const fromMeta = (row.image || '').trim();
    if (fromMeta) return this.#resolveThumbUrl(fromMeta);

    return this.#noImageUrl || null;
  }

  /**
   * @param {VariantMeta & { product?: LinkedOptionProduct, sku?: string, image?: string|null }} row
   * @returns {string}
   */
  #renderVariantHeader(row) {
    const imageUrl = this.#getVariantImageUrl(row);
    const title = escapeHtml(row.title);
    const skuHtml = row.sku
      ? `<span class="b2b-order__variant-sku">${escapeHtml(row.sku)}</span>`
      : '';

    const thumbHtml = imageUrl
      ? `<img
          class="b2b-order__variant-thumb"
          src="${escapeHtml(imageUrl)}"
          alt=""
          width="32"
          height="32"
          loading="lazy"
          decoding="async"
        />`
      : '';

    return `
      <div class="b2b-order__row-inner">
        ${thumbHtml}
        <div class="b2b-order__variant-details">
          <span class="b2b-order__variant-name">${title}</span>
          ${skuHtml}
        </div>
      </div>`;
  }

  /**
   * @param {string|null|undefined} dateStr
   * @returns {string|null}
   */
  #formatFutureDeliveryDate(dateStr) {
    if (typeof dateStr !== 'string' || !dateStr.trim()) return null;

    const parsed = new Date(`${dateStr.trim()}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed <= today) return null;

    return new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(parsed);
  }

  /**
   * @param {number|null|undefined} tier
   * @returns {number|null}
   */
  #stockFillPercent(tier) {
    switch (tier) {
      case 1:
        return 100;
      case 2:
        return 75;
      case 3:
        return 50;
      case 4:
        return 35;
      case 5:
        return 20;
      case 6:
        return 10;
      default:
        return null;
    }
  }

  /**
   * @param {StockStatus|undefined|null} configuredStatus
   * @returns {string|null}
   */
  #deliveryCaption(configuredStatus) {
    if (!configuredStatus?.tier) return null;
    const tier = Number(configuredStatus.tier);
    if (tier < 4 || tier > 6) return null;

    const formattedDate = this.#formatFutureDeliveryDate(configuredStatus.nextDeliveryDate);
    if (!formattedDate) return null;

    const localized = this.#stockTierLabels[String(tier)];
    const tier6 = this.#stockTierLabels['6'];
    const labelWithDate =
      localized?.label_with_date || tier6?.label_with_date || configuredStatus.label;
    if (!labelWithDate) return null;

    if (labelWithDate.includes('{date}')) {
      return labelWithDate.replace(/\{date\}/g, formattedDate);
    }

    return `${labelWithDate} ${formattedDate}`;
  }

  /**
   * @param {StockStatus|undefined|null} configuredStatus
   * @returns {string|null}
   */
  #configuredStockAriaLabel(configuredStatus) {
    if (configuredStatus?.updating) {
      return this.#t('stock_information_updating');
    }

    const delivery = this.#deliveryCaption(configuredStatus);
    if (delivery) return delivery;

    if (!configuredStatus?.tier && !configuredStatus?.label) return null;

    const tierKey = String(configuredStatus.tier ?? '');
    const localized = this.#stockTierLabels[tierKey];
    return localized?.label || configuredStatus.label || null;
  }

  /**
   * @param {VariantMeta & { product?: LinkedOptionProduct }} row
   * @returns {string}
   */
  #stockAriaLabel(row) {
    const configuredLabel = this.#configuredStockAriaLabel(row.product?.stock_status);
    if (configuredLabel) return configuredLabel;

    if (row.available_at_location === false) return this.#t('out_of_stock');

    if (row.inventory_quantity != null) {
      const qty = Number(row.inventory_quantity);
      if (!Number.isFinite(qty)) return '—';
      if (qty <= 0) return this.#t('units_zero');
      if (qty <= 10) return this.#t('units_count_low', { count: qty });
      return this.#t('units_count', { count: qty });
    }

    if (!row.available) return this.#t('out_of_stock');

    return '—';
  }

  /**
   * @param {VariantMeta & { product?: LinkedOptionProduct, sku?: string }} row
   * @returns {string}
   */
  #stockClass(row) {
    if (row.product?.stock_status?.updating) {
      return 'b2b-order__stock-status b2b-order__stock-status--updating';
    }

    if (row.product?.stock_status?.label || row.product?.stock_status?.tier) {
      return 'b2b-order__stock-status b2b-order__stock-status--configured';
    }

    if (row.available_at_location === false) {
      return 'b2b-order__stock-status b2b-order__stock-status--out';
    }

    if (row.inventory_quantity != null) {
      const qty = Number(row.inventory_quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        return 'b2b-order__stock-status b2b-order__stock-status--out';
      }
      if (qty <= 10) {
        return 'b2b-order__stock-status b2b-order__stock-status--low';
      }
      return 'b2b-order__stock-status';
    }

    if (!row.available) return 'b2b-order__stock-status b2b-order__stock-status--out';

    return 'b2b-order__stock-status';
  }

  /**
   * @param {VariantMeta & { product?: LinkedOptionProduct, sku?: string }} row
   * @returns {string}
   */
  #renderStockStatus(row) {
    const ariaLabel = this.#stockAriaLabel(row);
    const stockClass = this.#stockClass(row);
    const configuredStatus = row.product?.stock_status;

    if (configuredStatus?.updating) {
      return `
        <span class="${stockClass}" role="status" aria-label="${escapeHtml(ariaLabel)}">
          <span class="b2b-order__stock-label">${escapeHtml(ariaLabel)}</span>
        </span>`;
    }

    const hex =
      configuredStatus?.hex && /^#[0-9a-f]{6}$/i.test(configuredStatus.hex)
        ? configuredStatus.hex
        : '';
    const fill = this.#stockFillPercent(configuredStatus?.tier ?? null);
    const deliveryCaption = this.#deliveryCaption(configuredStatus);

    let fillPercent = fill;
    if (fillPercent == null) {
      if (row.available_at_location === false || !row.available) fillPercent = 10;
      else if (row.inventory_quantity != null) {
        const qty = Number(row.inventory_quantity);
        if (!Number.isFinite(qty) || qty <= 0) fillPercent = 10;
        else if (qty <= 10) fillPercent = 35;
        else fillPercent = 100;
      } else {
        fillPercent = 100;
      }
    }

    const styleParts = [
      `--stock-status-fill: ${fillPercent}%`,
      hex ? `--stock-status-color: ${escapeHtml(hex)}` : '',
    ].filter(Boolean);
    const style = ` style="${styleParts.join('; ')}"`;

    const infoHtml = deliveryCaption
      ? `
        <button
          type="button"
          class="b2b-order__stock-info"
          aria-label="${escapeHtml(deliveryCaption)}"
          aria-expanded="false"
          data-stock-tooltip-trigger
        >
          <span class="b2b-order__stock-info-icon" aria-hidden="true">i</span>
          <span class="b2b-order__stock-tooltip" role="tooltip">${escapeHtml(deliveryCaption)}</span>
        </button>`
      : '';

    return `
      <span class="${stockClass}"${style} role="status" aria-label="${escapeHtml(ariaLabel)}">
        <span class="b2b-order__stock-info-slot">${infoHtml}</span>
        <span class="b2b-order__stock-bar" aria-hidden="true">
          <span class="b2b-order__stock-bar-fill"></span>
        </span>
      </span>`;
  }

  /**
   * @returns {string}
   */
  #renderPackUnavailable() {
    return `
      <div class="b2b-order__pack-cell">
        <div class="b2b-order__pack-cell-inner">
          <span class="b2b-order__pack-unavailable" aria-hidden="true">—</span>
        </div>
      </div>`;
  }

  /**
   * @param {number|null} packSize
   * @param {string} packType
   * @param {string} variantId
   * @param {string} variantTitle
   * @param {boolean} disabled
   * @returns {string}
   */
  #renderPackInput(packSize, packType, variantId, variantTitle, disabled) {
    const sizeLabel =
      packType === 'unit'
        ? `<span class="b2b-order__pack-size">${escapeHtml(this.#t('per_unit'))}</span>`
        : packSize != null
          ? `<span class="b2b-order__pack-size">${escapeHtml(this.#t('pack_units', { count: packSize }))}</span>`
          : '';

    const label =
      packType === 'unit'
        ? this.#t('input_label_units', { title: variantTitle })
        : packType === 'inner'
          ? this.#t('input_label_inner_packs', { title: variantTitle })
          : this.#t('input_label_master_packs', { title: variantTitle });

    return `
      <div class="b2b-order__pack-cell">
        <div class="b2b-order__pack-cell-inner">
          <input
            type="number"
            class="b2b-order__pack-input"
            value="0"
            min="0"
            inputmode="numeric"
            data-variant-id="${escapeHtml(variantId)}"
            data-pack-type="${escapeHtml(packType)}"
            ${disabled ? 'disabled aria-disabled="true"' : ''}
            aria-label="${escapeHtml(label)}"
          />
          ${sizeLabel}
        </div>
      </div>`;
  }

  /**
   * @param {RenderedProductEntry} entry
   */
  #recalculateEntryQuantity(entry) {
    const innerUnits =
      entry.innerPackSize != null ? entry.innerPacks * entry.innerPackSize : 0;
    const masterUnits =
      entry.masterPackSize != null ? entry.masterPacks * entry.masterPackSize : 0;
    entry.quantity = entry.units + innerUnits + masterUnits;
  }

  /**
   * @param {LinkedOptionGroupData | null} data
   */
  #renderTable(data) {
    const rows = this.#buildVariantRows(data);

    if (rows.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    this.#renderedProducts.clear();

    const tbody = this.querySelector('[data-ref="tbody"]');
    if (!tbody) return;

    const showMasterColumn = true;
    this.#showMasterColumn = showMasterColumn;

    const headerRow = this.querySelector('[data-ref="headerRow"]');
    const masterHeader = headerRow?.querySelector('th:last-child');
    if (masterHeader instanceof HTMLElement) {
      masterHeader.hidden = false;
    }

    const rowsHtml = rows
      .map((row) => {
        const rowKey = String(row.rowKey ?? row.id);
        const cartVariantId = String(row.cartVariantId ?? row.id);
        const innerPackSize = parsePackSize(row.inner_quantity);
        const masterPackSize = parsePackSize(row.master_quantity);
        const masterSameAsInner = isSamePackQuantity(row.inner_quantity, row.master_quantity);
        const rowMasterPackSize =
          showMasterColumn && !masterSameAsInner ? masterPackSize : null;
        const rowUnavailable =
          row.available_at_location === false || !row.available;
        const unitDisabled = rowUnavailable;
        const innerDisabled = rowUnavailable || innerPackSize == null;
        const masterDisabled = rowUnavailable || masterPackSize == null || masterSameAsInner;
        const rowSearchTerms = [row.title, row.sku, rowKey, cartVariantId]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        const unitPrice = parseProductPrice(row.product);
        const productCurrency = (row.product?.currency_code || '').trim().toUpperCase();
        if (unitPrice != null && productCurrency) {
          this.#currencyCode = productCurrency;
        }

        this.#renderedProducts.set(rowKey, {
          product: row.product,
          rowValue: row.title,
          units: 0,
          innerPacks: 0,
          masterPacks: 0,
          innerPackSize,
          masterPackSize: rowMasterPackSize,
          quantity: 0,
          sku: row.sku || '',
          price: unitPrice,
          variantId: cartVariantId,
        });

        const rowClass = rowUnavailable
          ? 'b2b-order__row b2b-order__row--disabled'
          : 'b2b-order__row';

        return `
          <tr
            class="${rowClass}"
            data-variant-id="${escapeHtml(rowKey)}"
            data-search-terms="${escapeHtml(rowSearchTerms)}"
          >
            <th scope="row" class="b2b-order__row-header">
              ${this.#renderVariantHeader(row)}
            </th>
            <td class="b2b-order__cell b2b-order__cell--stock${rowUnavailable ? ' b2b-order__cell--unavailable' : ''}">
              ${this.#renderStockStatus(row)}
            </td>
            <td class="b2b-order__cell b2b-order__cell--pack${unitDisabled ? ' b2b-order__cell--disabled' : ''}">
              ${this.#renderPackInput(null, 'unit', rowKey, row.title, unitDisabled)}
            </td>
            <td class="b2b-order__cell b2b-order__cell--pack${innerDisabled ? ' b2b-order__cell--disabled' : ''}">
              ${this.#renderPackInput(innerPackSize, 'inner', rowKey, row.title, innerDisabled)}
            </td>
            ${
              showMasterColumn
                ? `<td class="b2b-order__cell b2b-order__cell--pack${masterDisabled ? ' b2b-order__cell--disabled' : ''}">
              ${
                masterSameAsInner
                  ? this.#renderPackUnavailable()
                  : this.#renderPackInput(masterPackSize, 'master', rowKey, row.title, masterDisabled)
              }
            </td>`
                : ''
            }
          </tr>`;
      })
      .join('');

    tbody.innerHTML = rowsHtml;
    this.removeAttribute('hidden');
    this.#updateTotals();
  }

  #bindEvents() {
    this.#inputAbort?.abort();
    this.#inputAbort = new AbortController();
    const { signal } = this.#inputAbort;

    this.addEventListener('input', this.#handlePackInput.bind(this), { signal });
    this.addEventListener('change', this.#handlePackChange.bind(this), { signal });
    this.addEventListener('click', this.#handleStockTooltipClick.bind(this), { signal });
    this.addEventListener('keydown', this.#handleStockTooltipKeydown.bind(this), { signal });

    const searchInput = this.querySelector('[data-ref="searchInput"]');
    if (searchInput) {
      searchInput.addEventListener('input', this.#handleSearch.bind(this), { signal });
    }

    const btnAdd = this.querySelector('[data-ref="btnAdd"]');
    if (btnAdd) {
      btnAdd.addEventListener('click', this.#handleAddToBasket.bind(this), { signal });
    }

    const btnClear = this.querySelector('[data-ref="btnClear"]');
    if (btnClear) {
      btnClear.addEventListener('click', this.#handleClear.bind(this), { signal });
    }

    const btnLoad = this.querySelector('[data-ref="btnLoad"]');
    if (btnLoad) {
      btnLoad.addEventListener('click', this.#handleLoadLastOrder.bind(this), { signal });
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleStockTooltipClick(event) {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-stock-tooltip-trigger]')
      : null;
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) {
      this.#closeAllStockTooltips();
      return;
    }

    event.preventDefault();
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.#closeAllStockTooltips();
    if (!isOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('b2b-order__stock-info--open');
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleStockTooltipKeydown(event) {
    if (event.key !== 'Escape') return;
    this.#closeAllStockTooltips();
  }

  #closeAllStockTooltips() {
    this.querySelectorAll('[data-stock-tooltip-trigger]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
      el.classList.remove('b2b-order__stock-info--open');
    });
  }

  /**
   * @param {string} variantId
   * @returns {RenderedProductEntry | undefined}
   */
  #syncEntryFromRow(variantId) {
    const entry = this.#renderedProducts.get(variantId);
    if (!entry) return undefined;

    const innerInput = this.querySelector(
      `[data-variant-id="${CSS.escape(variantId)}"][data-pack-type="inner"]`
    );
    const masterInput = this.querySelector(
      `[data-variant-id="${CSS.escape(variantId)}"][data-pack-type="master"]`
    );
    const unitInput = this.querySelector(
      `[data-variant-id="${CSS.escape(variantId)}"][data-pack-type="unit"]`
    );

    if (unitInput instanceof HTMLInputElement && !unitInput.disabled) {
      const raw = parseInt(unitInput.value, 10);
      entry.units = isNaN(raw) || raw < 0 ? 0 : raw;
      if (unitInput.value !== String(entry.units)) {
        unitInput.value = String(entry.units);
      }
      unitInput.classList.toggle('b2b-order__pack-input--active', entry.units > 0);
    } else {
      entry.units = 0;
    }

    if (innerInput instanceof HTMLInputElement && !innerInput.disabled) {
      const raw = parseInt(innerInput.value, 10);
      entry.innerPacks = isNaN(raw) || raw < 0 ? 0 : raw;
      if (innerInput.value !== String(entry.innerPacks)) {
        innerInput.value = String(entry.innerPacks);
      }
      innerInput.classList.toggle('b2b-order__pack-input--active', entry.innerPacks > 0);
    } else {
      entry.innerPacks = 0;
    }

    if (masterInput instanceof HTMLInputElement && !masterInput.disabled) {
      const raw = parseInt(masterInput.value, 10);
      entry.masterPacks = isNaN(raw) || raw < 0 ? 0 : raw;
      if (masterInput.value !== String(entry.masterPacks)) {
        masterInput.value = String(entry.masterPacks);
      }
      masterInput.classList.toggle('b2b-order__pack-input--active', entry.masterPacks > 0);
    } else {
      entry.masterPacks = 0;
    }

    this.#recalculateEntryQuantity(entry);
    return entry;
  }

  /** @param {Event} event */
  #handlePackInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.classList.contains('b2b-order__pack-input')) return;

    const variantId = input.dataset.variantId ?? '';
    if (!variantId) return;

    this.#syncEntryFromRow(variantId);
    this.#updateTotals();
  }

  /** @param {Event} event */
  #handlePackChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.classList.contains('b2b-order__pack-input')) return;

    const variantId = input.dataset.variantId ?? '';
    if (!variantId) return;

    this.#syncEntryFromRow(variantId);
  }

  #updateTotals() {
    let totalUnits = 0;
    let totalLines = 0;
    let orderTotalMinor = 0;
    let hasQtyWithoutPrice = false;

    for (const entry of this.#renderedProducts.values()) {
      if (entry.quantity <= 0) continue;

      totalUnits += entry.quantity;
      totalLines += 1;

      const sellUnit = entry.price;

      if (sellUnit == null) {
        hasQtyWithoutPrice = true;
        continue;
      }

      const currency = this.#currencyCode || (this.dataset.currency || '').trim().toUpperCase();
      const sellMinor = convertMoneyToMinorUnits(String(sellUnit), currency);
      if (sellMinor == null) {
        hasQtyWithoutPrice = true;
        continue;
      }

      orderTotalMinor += sellMinor * entry.quantity;
    }

    const unitsEl = this.querySelector('[data-ref="totalUnits"]');
    const linesEl = this.querySelector('[data-ref="totalLines"]');
    const orderValueEl = this.querySelector('[data-ref="orderValue"]');
    const btnAdd = this.querySelector('[data-ref="btnAdd"]');
    const btnClear = this.querySelector('[data-ref="btnClear"]');

    if (unitsEl) unitsEl.textContent = String(totalUnits);
    if (linesEl) linesEl.textContent = String(totalLines);

    const moneyFormat = this.dataset.moneyFormat || '';
    const currency = this.#currencyCode || (this.dataset.currency || '').trim().toUpperCase();

    if (orderValueEl) {
      if (hasQtyWithoutPrice && totalUnits > 0) {
        orderValueEl.textContent = '—';
      } else if (moneyFormat && currency) {
        orderValueEl.textContent = formatMoney(orderTotalMinor, moneyFormat, currency);
      } else {
        orderValueEl.textContent = '—';
      }
    }

    const hasItems = totalUnits > 0;
    if (btnAdd instanceof HTMLButtonElement) {
      btnAdd.disabled = !hasItems;
      btnAdd.setAttribute('aria-disabled', String(!hasItems));
    }
    if (btnClear instanceof HTMLButtonElement) {
      if (hasItems) {
        btnClear.removeAttribute('hidden');
      } else {
        btnClear.setAttribute('hidden', '');
      }
    }
  }

  #handleClear() {
    const hasItems = [...this.#renderedProducts.values()].some((entry) => entry.quantity > 0);
    if (!hasItems) return;

    this.#resetQuantities();
  }

  /** @param {Event} event */
  #handleSearch(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#applySearch(input.value);
  }

  /** @param {string} rawQuery */
  #applySearch(rawQuery) {
    const query = rawQuery.trim().toLowerCase();
    const rows = this.querySelectorAll('[data-ref="tbody"] .b2b-order__row');
    let visibleCount = 0;

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;

      const terms = (row.dataset.searchTerms ?? '').toLowerCase();
      const matches = query === '' || terms.includes(query);

      row.hidden = !matches;
      if (matches) visibleCount++;
    }

    const noResults = this.querySelector('[data-ref="noResults"]');
    if (noResults) {
      noResults.classList.toggle('b2b-order__no-results--visible', visibleCount === 0);
    }
  }

  #syncAllEntriesFromDom() {
    for (const rowKey of this.#renderedProducts.keys()) {
      this.#syncEntryFromRow(rowKey);
    }
  }

  async #handleAddToBasket() {
    this.#syncAllEntriesFromDom();

    const items = [];
    for (const entry of this.#renderedProducts.values()) {
      if (entry.quantity > 0 && entry.variantId) {
        items.push({ id: Number(entry.variantId), quantity: entry.quantity });
      }
    }

    if (items.length === 0) return;

    const btnAdd = this.querySelector('[data-ref="btnAdd"]');
    const errorMsg = this.querySelector('[data-ref="errorMsg"]');

    if (btnAdd instanceof HTMLButtonElement) {
      btnAdd.disabled = true;
      btnAdd.setAttribute('aria-disabled', 'true');
    }
    if (errorMsg) {
      errorMsg.classList.remove('b2b-order__error--visible');
    }

    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';

    const cartSections = Array.from(
      document.querySelectorAll('cart-items-component[data-section-id]')
    )
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: cartSections }),
      });

      const json = await response.json();

      if (!response.ok || json.status) {
        throw new Error(json.message || json.description || this.#t('error_add_to_basket'));
      }

      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

      document.dispatchEvent(
        new CartAddEvent(json, this.id || '', {
          source: 'product-form-component',
          itemCount: totalQuantity,
          sections: json.sections,
        })
      );

      this.#resetQuantities();
    } catch (err) {
      const msg = err instanceof Error ? err.message : this.#t('error_add_to_basket');
      if (errorMsg) {
        errorMsg.textContent = msg;
        errorMsg.classList.add('b2b-order__error--visible');
      }
    } finally {
      this.#updateTotals();
    }
  }

  #resetQuantities() {
    for (const entry of this.#renderedProducts.values()) {
      entry.units = 0;
      entry.innerPacks = 0;
      entry.masterPacks = 0;
      entry.quantity = 0;
    }

    for (const input of this.querySelectorAll('.b2b-order__pack-input')) {
      if (input instanceof HTMLInputElement) {
        input.value = '0';
        input.classList.remove('b2b-order__pack-input--active');
      }
    }

    this.#updateTotals();
  }

  /**
   * @param {string} rowKey
   * @param {number} quantity
   * @returns {boolean}
   */
  #applyLoadedQuantityToRow(rowKey, quantity) {
    const entry = this.#renderedProducts.get(rowKey);
    if (!entry) return false;

    const unitInput = this.querySelector(
      `[data-variant-id="${CSS.escape(rowKey)}"][data-pack-type="unit"]`
    );
    if (!(unitInput instanceof HTMLInputElement) || unitInput.disabled) return false;

    const innerInput = this.querySelector(
      `[data-variant-id="${CSS.escape(rowKey)}"][data-pack-type="inner"]`
    );
    const masterInput = this.querySelector(
      `[data-variant-id="${CSS.escape(rowKey)}"][data-pack-type="master"]`
    );

    let remaining = Math.floor(quantity);
    if (remaining <= 0) return false;

    let masterPacks = 0;
    let innerPacks = 0;

    const masterPackSize = entry.masterPackSize;
    const innerPackSize = entry.innerPackSize;

    const masterAvailable =
      this.#showMasterColumn &&
      masterInput instanceof HTMLInputElement &&
      !masterInput.disabled &&
      masterPackSize != null &&
      masterPackSize > 0;

    const innerAvailable =
      innerInput instanceof HTMLInputElement &&
      !innerInput.disabled &&
      innerPackSize != null &&
      innerPackSize > 0;

    if (masterAvailable) {
      masterPacks = Math.floor(remaining / masterPackSize);
      remaining -= masterPacks * masterPackSize;
    }

    if (innerAvailable) {
      innerPacks = Math.floor(remaining / innerPackSize);
      remaining -= innerPacks * innerPackSize;
    }

    if (masterAvailable && masterInput instanceof HTMLInputElement) {
      masterInput.value = String(masterPacks);
    }

    if (innerAvailable && innerInput instanceof HTMLInputElement) {
      innerInput.value = String(innerPacks);
    }

    unitInput.value = String(remaining);
    this.#syncEntryFromRow(rowKey);
    return true;
  }

  async #handleLoadLastOrder() {
    if (!this.#isCustomerLoggedIn()) return;
    if (this.dataset.initialized !== 'true') return;

    const btnLoad = this.querySelector('[data-ref="btnLoad"]');
    const errorMsg = this.querySelector('[data-ref="errorMsg"]');
    if (!(btnLoad instanceof HTMLButtonElement)) return;

    btnLoad.disabled = true;
    btnLoad.setAttribute('aria-disabled', 'true');

    if (errorMsg) {
      errorMsg.textContent = '';
      errorMsg.classList.remove('b2b-order__error--visible');
    }

    try {
      const proxyPrefix = this.dataset.proxyPrefix || '/apps/pmecake';
      const { order, error } = await fetchCustomerLastOrder(proxyPrefix);

      if (error) {
        if (errorMsg) {
          errorMsg.textContent = this.#t('error_load_last_order');
          errorMsg.classList.add('b2b-order__error--visible');
        }
        return;
      }

      if (!order || order.lines.length === 0) {
        if (errorMsg) {
          errorMsg.textContent = this.#t('error_no_previous_order');
          errorMsg.classList.add('b2b-order__error--visible');
        }
        return;
      }

      this.#resetQuantities();

      /** @type {Map<string, string>} */
      const rowKeyByVariantId = new Map();
      /** @type {Map<string, string>} */
      const rowKeyByProductId = new Map();

      for (const [rowKey, entry] of this.#renderedProducts) {
        if (entry.variantId) {
          rowKeyByVariantId.set(String(entry.variantId), rowKey);
        }
        rowKeyByProductId.set(String(rowKey), rowKey);
      }

      let matchedCount = 0;

      for (const line of order.lines) {
        const variantId = String(line.variantId ?? '');
        const productId = String(line.productId ?? '');
        const quantity = Number(line.quantity ?? 0);
        if (!Number.isFinite(quantity) || quantity <= 0) continue;

        const rowKey =
          (variantId && rowKeyByVariantId.get(variantId)) ||
          (productId && rowKeyByProductId.get(productId)) ||
          null;

        if (!rowKey) continue;

        if (this.#applyLoadedQuantityToRow(rowKey, quantity)) {
          matchedCount += 1;
        }
      }

      this.#updateTotals();

      if (matchedCount === 0 && errorMsg) {
        errorMsg.textContent = this.#t('error_no_matching_products');
        errorMsg.classList.add('b2b-order__error--visible');
      }
    } catch {
      if (errorMsg) {
        errorMsg.textContent = this.#t('error_could_not_load_order');
        errorMsg.classList.add('b2b-order__error--visible');
      }
    } finally {
      btnLoad.disabled = false;
      btnLoad.setAttribute('aria-disabled', 'false');
    }
  }

  /**
   * @param {Map<string, { sku: string, price: number, variantId: string }>} hydrateMap
   */
  hydrateProducts(hydrateMap) {
    for (const [id, enriched] of hydrateMap) {
      const entry = this.#renderedProducts.get(id);
      if (!entry) continue;
      if (enriched.sku) entry.sku = enriched.sku;
      if (enriched.price != null) entry.price = enriched.price;
      if (enriched.variantId) {
        entry.variantId = enriched.variantId;
        for (const input of this.querySelectorAll(
          `[data-variant-id="${CSS.escape(id)}"].b2b-order__pack-input`
        )) {
          if (input instanceof HTMLInputElement) {
            input.dataset.variantId = enriched.variantId;
          }
        }
      }
    }
    this.#updateTotals();
  }

  /** @returns {ReadonlyMap<string, RenderedProductEntry>} */
  get renderedProducts() {
    return this.#renderedProducts;
  }
}

if (!customElements.get('linked-options-b2b-matrix-table')) {
  customElements.define('linked-options-b2b-matrix-table', LinkedOptionsB2bMatrixTable);
}
